// Tiny Web Audio cues for the scanner — no asset files, no deps.
// Global mute is a single persisted preference (localStorage) so one toggle controls sound everywhere.
import { useState, useEffect } from "react";

const SOUND_KEY = "fas_scan_sound";
const _subs = new Set();

export function isSoundOn() {
  try { return localStorage.getItem(SOUND_KEY) !== "off"; } catch { return true; }
}
export function setSoundOn(on) {
  try { localStorage.setItem(SOUND_KEY, on ? "on" : "off"); } catch { /* ignore */ }
  _subs.forEach((fn) => fn(on));
}
export function useSoundToggle() {
  const [on, setOn] = useState(isSoundOn());
  useEffect(() => {
    const fn = (v) => setOn(v);
    _subs.add(fn);
    const onStorage = (e) => { if (e.key === SOUND_KEY) setOn(isSoundOn()); };
    window.addEventListener("storage", onStorage);
    return () => { _subs.delete(fn); window.removeEventListener("storage", onStorage); };
  }, []);
  return [on, () => setSoundOn(!isSoundOn())];
}

let ctx = null;
function ac() {
  if (!ctx) {
    const C = window.AudioContext || window.webkitAudioContext;
    if (C) ctx = new C();
  }
  return ctx;
}
function tone(freq, durMs, type = "sine", gainVal = 0.12) {
  const a = ac();
  if (!a) return;
  if (a.state === "suspended") a.resume();
  const osc = a.createOscillator();
  const gain = a.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  gain.gain.value = gainVal;
  osc.connect(gain);
  gain.connect(a.destination);
  const t = a.currentTime;
  osc.start(t);
  gain.gain.setValueAtTime(gainVal, t);
  gain.gain.exponentialRampToValueAtTime(0.0001, t + durMs / 1000);
  osc.stop(t + durMs / 1000);
}
// THE two cues. Nothing imports these directly except lib/scanFeedback.jsx — the one shared piece
// that pairs them with the green/red tint flash so a scan answer always looks AND sounds the same.
export function beepAccept() {
  if (!isSoundOn()) return;
  tone(880, 90, "sine");
  setTimeout(() => tone(1320, 110, "sine"), 90);
}
export function beepRefuse() {
  if (!isSoundOn()) return;
  tone(220, 260, "square", 0.14);
}

// SPOKEN CONFIRMATION — his rule for the floor, 2026-10-08: *"a beep, a green flash, a spoken
// 'added'"*. Obeys the same single sound toggle as the beeps, and is silent on a tablet with no
// voice installed rather than throwing. Each new word cancels the one before it so a fast run of
// scans never queues up a backlog of talking.
export function speak(text) {
  if (!isSoundOn() || !text) return;
  try {
    const synth = window.speechSynthesis;
    if (!synth || typeof window.SpeechSynthesisUtterance !== "function") return;
    synth.cancel();
    const u = new window.SpeechSynthesisUtterance(String(text));
    u.rate = 1.1;
    u.volume = 1;
    synth.speak(u);
  } catch { /* no voice on this device — the beep and the flash still land */ }
}

// iOS/Safari suspend the AudioContext until a real user gesture resumes it. Call
// this from a tap handler (camera-start, submit, set-destination) so the later
// camera-driven success/error beeps actually play — the audible "got it" is what
// stops the operator holding a tag in frame (the root of the re-read glitch).
export function unlockAudio() {
  try {
    const a = ac();
    if (a && a.state === "suspended") a.resume();
  } catch { /* ignore */ }
}
